import React, { useEffect, useMemo, useState } from 'react';
import CustomDropdown from '../CustomDropdown';
import { VendorCombobox } from '../VendorCombobox';
import {
  ImportReviewGroupedList,
  type PendingCategoryApply,
} from './ImportReviewGroupedList';
import { ImportIntelligenceSummary } from './ImportIntelligenceSummary';
import { ImportCleanupSuggestions } from './ImportCleanupSuggestions';
import {
  buildReviewHierarchy,
  countDistinctGroups,
} from '../../utils/importReviewGrouping';
import {
  allCategoryKeys,
  allGroupKeys,
  getVisibleModificationIndices,
  importReviewCategoryDomId,
  importReviewFiltersDomId,
  importReviewGroupDomId,
  itemNeedsReview,
  type CleanupSuggestion,
  type ImportReviewFilter,
} from '../../utils/importIntelligence';
import type { ParsedModification } from './importReviewTypes';
import type { SmartSuggestionFields } from './importConstants';

function scrollImportReviewTarget(elementId: string): void {
  window.requestAnimationFrame(() => {
    window.setTimeout(() => {
      const el = document.getElementById(elementId);
      if (!el) return;
      el.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' });
      el.classList.add('ring-2', 'ring-orange-500/60');
      window.setTimeout(() => {
        el.classList.remove('ring-2', 'ring-orange-500/60');
      }, 1200);
    }, 50);
  });
}

interface ImportReviewStepProps {
  parsedModifications: ParsedModification[];
  excludedIndices: Set<number>;
  collapsedCategories: Set<string>;
  collapsedGroups: Set<string>;
  parseMode: 'standard' | 'receipt';
  receiptConfirmed: boolean;
  setReceiptConfirmed: (v: boolean) => void;
  availableCategories: string[];
  availableVendors: string[];
  smartSuggestions: Record<number, SmartSuggestionFields>;
  selectedMods: Set<number>;
  selectMode: boolean;
  setSelectMode: (v: boolean) => void;
  bulkEditMode: 'category' | 'vendor' | 'cost' | null;
  setBulkEditMode: (v: 'category' | 'vendor' | 'cost' | null) => void;
  setSelectedMods: React.Dispatch<React.SetStateAction<Set<number>>>;
  setCollapsedCategories: React.Dispatch<React.SetStateAction<Set<string>>>;
  setCollapsedGroups: React.Dispatch<React.SetStateAction<Set<string>>>;
  setParsedModifications: React.Dispatch<React.SetStateAction<ParsedModification[]>>;
  pendingCategoryApply: PendingCategoryApply | null;
  onCategoryChange: (index: number, newCategory: string, previousCategory: string) => void;
  onApplyCategoryToSimilar: (changedIndex: number, newCategory: string, similarIndices: number[]) => void;
  onDismissCategoryApply: () => void;
  errors: string[];
  onToggleCategory: (category: string) => void;
  onToggleGroup: (key: string) => void;
  onToggleIncluded: (index: number) => void;
  onEdit: (index: number, field: keyof ParsedModification, value: unknown) => void;
  onRemove: (index: number) => void;
  onSkipDuplicates: () => void;
  onApplySuggestions: () => void;
  getIncludedCount: () => number;
}

export const ImportReviewStep: React.FC<ImportReviewStepProps> = ({
  parsedModifications,
  excludedIndices,
  collapsedCategories,
  collapsedGroups,
  parseMode,
  receiptConfirmed,
  setReceiptConfirmed,
  availableCategories,
  availableVendors,
  smartSuggestions,
  selectedMods,
  selectMode,
  setSelectMode,
  bulkEditMode,
  setBulkEditMode,
  setSelectedMods,
  setCollapsedCategories,
  setCollapsedGroups,
  setParsedModifications,
  pendingCategoryApply,
  onCategoryChange,
  onApplyCategoryToSimilar,
  onDismissCategoryApply,
  errors,
  onToggleCategory,
  onToggleGroup,
  onToggleIncluded,
  onEdit,
  onRemove,
  onSkipDuplicates,
  onApplySuggestions,
  getIncludedCount,
}) => {
  const [summaryCollapsed, setSummaryCollapsed] = useState(true);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [reviewFilter, setReviewFilter] = useState<ImportReviewFilter>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [bulkVendorDraft, setBulkVendorDraft] = useState('');
  const [defaultFilterApplied, setDefaultFilterApplied] = useState(false);

  useEffect(() => {
    if (bulkEditMode !== 'vendor') {
      setBulkVendorDraft('');
    }
  }, [bulkEditMode]);

  useEffect(() => {
    if (parsedModifications.length >= 50) {
      setSummaryCollapsed(true);
    }
  }, [parsedModifications.length]);

  const needsLookCount = useMemo(
    () =>
      parsedModifications.filter(
        (m, i) =>
          !excludedIndices.has(i) && itemNeedsReview(m, availableCategories, availableVendors)
      ).length,
    [parsedModifications, excludedIndices, availableCategories, availableVendors]
  );

  useEffect(() => {
    if (defaultFilterApplied || parsedModifications.length === 0) return;
    if (needsLookCount > 0) {
      setReviewFilter('warnings');
    }
    setDefaultFilterApplied(true);
  }, [defaultFilterApplied, parsedModifications.length, needsLookCount]);

  const duplicateCount = parsedModifications.filter((m) => m.duplicate_warning && m.duplicate_resolution !== 'separate').length;
  const categoryCount = new Set(parsedModifications.map((m) => m.category)).size;
  const groupCount = countDistinctGroups(parsedModifications);
  const skippedCount = excludedIndices.size;
  const includedCount = getIncludedCount();

  const hierarchy = useMemo(
    () => buildReviewHierarchy(parsedModifications),
    [parsedModifications]
  );

  const visibleIndices = useMemo(
    () =>
      getVisibleModificationIndices(
        parsedModifications,
        reviewFilter,
        searchQuery,
        excludedIndices,
        availableCategories,
        availableVendors
      ),
    [
      parsedModifications,
      reviewFilter,
      searchQuery,
      excludedIndices,
      availableCategories,
      availableVendors,
    ]
  );

  const collapseAll = () => {
    setCollapsedCategories(new Set(allCategoryKeys(hierarchy)));
    setCollapsedGroups(new Set(allGroupKeys(hierarchy)));
  };

  const expandAll = () => {
    setCollapsedCategories(new Set());
    setCollapsedGroups(new Set());
  };

  const handleCleanupAction = (suggestion: CleanupSuggestion) => {
    if (suggestion.action === 'filter-warnings') {
      setReviewFilter('warnings');
      setSearchQuery('');
      scrollImportReviewTarget(importReviewFiltersDomId());
    } else if (suggestion.action === 'filter-uncategorized') {
      setReviewFilter('uncategorized');
      setSearchQuery('');
      scrollImportReviewTarget(importReviewFiltersDomId());
    } else if (suggestion.action === 'exclude-duplicates') {
      onSkipDuplicates();
    } else if (suggestion.action === 'expand-section' && suggestion.meta?.category) {
      const category = suggestion.meta.category;
      const groupLabel = suggestion.meta.groupLabel?.trim() || '';
      setReviewFilter('all');
      setSearchQuery(groupLabel);
      setCollapsedCategories((prev) => {
        const next = new Set(prev);
        next.delete(category);
        return next;
      });
      if (suggestion.meta.groupKey) {
        setCollapsedGroups((prev) => {
          const next = new Set(prev);
          next.delete(suggestion.meta!.groupKey!);
          return next;
        });
      }
      const targetId = suggestion.meta.groupKey
        ? importReviewGroupDomId(suggestion.meta.groupKey)
        : importReviewCategoryDomId(category);
      window.requestAnimationFrame(() => {
        window.setTimeout(() => {
          const target =
            document.getElementById(targetId) ||
            document.getElementById(importReviewCategoryDomId(category)) ||
            document.getElementById(importReviewFiltersDomId());
          if (!target) return;
          target.scrollIntoView({ behavior: 'smooth', block: 'start' });
          target.classList.add('ring-2', 'ring-orange-500/60');
          window.setTimeout(() => {
            target.classList.remove('ring-2', 'ring-orange-500/60');
          }, 1200);
        }, 50);
      });
    }
  };

  const filteredCount =
    visibleIndices?.size ??
    parsedModifications.filter((_, i) => !excludedIndices.has(i)).length;

  const isFiltering = reviewFilter !== 'all' || searchQuery.trim().length > 0;

  const filterChipClass = (active: boolean) =>
    `text-xs px-2.5 py-1.5 rounded-full border whitespace-nowrap shrink-0 min-h-[44px] sm:min-h-[36px] ${
      active
        ? 'border-orange-500/60 bg-orange-900/25 text-orange-200'
        : 'border-white/[0.10] text-gray-400 hover:text-white hover:border-white/[0.18]'
    }`;

  const exitSelectMode = () => {
    setSelectMode(false);
    setSelectedMods(new Set());
    setBulkEditMode(null);
  };

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-lg font-semibold text-white">Your build list is organized</h3>
        <p className="text-sm text-gray-400 mt-1">
          Check anything that needs a look, then add the parts you want to keep.
        </p>
      </div>

      <div className="form-section !p-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-400 !space-y-0">
        <span>
          <span className="text-white font-medium">{parsedModifications.length}</span> parts found
        </span>
        <span>
          <span className="text-white font-medium">{categoryCount}</span> categories
        </span>
        {groupCount > 0 && (
          <span>
            <span className="text-white font-medium">{groupCount}</span> sections
          </span>
        )}
        {duplicateCount > 0 && (
          <span className="text-yellow-300">
            <span className="font-medium">{duplicateCount}</span> possible matches
          </span>
        )}
        {needsLookCount > 0 && (
          <span className="text-amber-300">
            <span className="font-medium">{needsLookCount}</span> need a look
          </span>
        )}
        {skippedCount > 0 && (
          <span>
            <span className="font-medium text-gray-300">{skippedCount}</span> excluded
          </span>
        )}
        <span className="text-orange-300">
          <span className="font-medium">{includedCount}</span> to add
        </span>
      </div>

      <ImportIntelligenceSummary
        modifications={parsedModifications}
        collapsed={summaryCollapsed}
        onToggleCollapsed={() => setSummaryCollapsed((v) => !v)}
      />

      <ImportCleanupSuggestions
        modifications={parsedModifications}
        excludedIndices={excludedIndices}
        availableCategories={availableCategories}
        availableVendors={availableVendors}
        onAction={handleCleanupAction}
      />

      {parseMode === 'receipt' && (
        <div className="p-4 bg-amber-900/40 border border-amber-700 rounded-lg">
          <p className="text-amber-200 font-medium text-sm">Receipt / invoice import</p>
          <p className="text-amber-300/90 text-xs mt-1">
            Review every line. Totals, tax, and shipping are not imported as parts.
          </p>
          <label className="flex items-start gap-2 mt-3 cursor-pointer min-h-[44px]">
            <input
              type="checkbox"
              checked={receiptConfirmed}
              onChange={(e) => setReceiptConfirmed(e.target.checked)}
              className="mt-0.5 rounded border-white/[0.14] text-orange-500 focus:ring-orange-500"
            />
            <span className="text-sm text-amber-100">
              I have reviewed all line items and removed non-parts (tax, shipping, subtotals).
            </span>
          </label>
        </div>
      )}

      <div id={importReviewFiltersDomId()} className="space-y-3 scroll-mt-4">
        <div className="flex items-center justify-between gap-2">
          <button
            type="button"
            className="sm:hidden text-xs px-3 py-2 rounded-lg border border-white/[0.10] text-gray-300 min-h-[40px]"
            onClick={() => setFiltersOpen((v) => !v)}
            aria-expanded={filtersOpen}
          >
            {filtersOpen ? 'Hide filters' : 'Filters & search'}
          </button>
          <div className="hidden sm:block flex-1" />
          <button
            type="button"
            onClick={() => (selectMode ? exitSelectMode() : setSelectMode(true))}
            className={`text-xs px-3 py-2 rounded-lg border min-h-[40px] ${
              selectMode
                ? 'border-orange-500/60 text-orange-200 bg-orange-900/20'
                : 'border-white/[0.10] text-gray-300'
            }`}
            aria-pressed={selectMode}
          >
            {selectMode ? 'Done selecting' : 'Select items'}
          </button>
        </div>

        <div className={`${filtersOpen ? 'block' : 'hidden'} sm:block space-y-3`}>
          <div className="relative">
            <input
              type="search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search parts, brands, sections…"
              className="input pr-10 text-sm"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-gray-400 hover:text-white px-2 py-1"
                aria-label="Clear search"
              >
                Clear
              </button>
            )}
          </div>

          {isFiltering && (
            <p className="text-xs text-gray-400">
              Showing {filteredCount} of {parsedModifications.length} parts
            </p>
          )}

          <div className="flex flex-wrap gap-2">
            <button type="button" onClick={() => setReviewFilter('all')} className={filterChipClass(reviewFilter === 'all')}>
              All parts
            </button>
            <button
              type="button"
              onClick={() => setReviewFilter('warnings')}
              className={filterChipClass(reviewFilter === 'warnings')}
            >
              Needs a look{needsLookCount > 0 ? ` (${needsLookCount})` : ''}
            </button>
            <button
              type="button"
              onClick={() => setReviewFilter('duplicates')}
              className={filterChipClass(reviewFilter === 'duplicates')}
            >
              Duplicates{duplicateCount > 0 ? ` (${duplicateCount})` : ''}
            </button>
            <button
              type="button"
              onClick={() => setReviewFilter('uncategorized')}
              className={filterChipClass(reviewFilter === 'uncategorized')}
            >
              Uncategorized
            </button>
            <button
              type="button"
              onClick={() => setReviewFilter('excluded')}
              className={filterChipClass(reviewFilter === 'excluded')}
            >
              Excluded{skippedCount > 0 ? ` (${skippedCount})` : ''}
            </button>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={expandAll}
              className="text-xs px-2.5 py-1.5 rounded border border-white/[0.10] text-gray-400 hover:text-white min-h-[36px]"
            >
              Expand all
            </button>
            <button
              type="button"
              onClick={collapseAll}
              className="text-xs px-2.5 py-1.5 rounded border border-white/[0.10] text-gray-400 hover:text-white min-h-[36px]"
            >
              Collapse all
            </button>
            {duplicateCount > 0 && (
              <button
                type="button"
                onClick={onSkipDuplicates}
                className="text-xs px-2.5 py-1.5 rounded border border-yellow-700/60 text-yellow-300 hover:bg-yellow-900/20 min-h-[36px]"
              >
                Exclude {duplicateCount} duplicate{duplicateCount === 1 ? '' : 's'}
              </button>
            )}
            <button
              type="button"
              onClick={onApplySuggestions}
              className="text-xs px-2.5 py-1.5 rounded border border-white/[0.10] text-gray-300 hover:text-white min-h-[36px]"
            >
              Apply suggestions
            </button>
          </div>
        </div>
      </div>

      {selectMode && (
        <div className="form-section !p-3 flex flex-wrap items-center gap-2 !space-y-0">
          <span className="text-xs text-gray-400">
            {selectedMods.size > 0 ? `${selectedMods.size} selected` : 'Tap items to select'}
          </span>
          <CustomDropdown
            value={bulkEditMode || ''}
            onChange={(value) => setBulkEditMode(value as 'category' | 'vendor' | 'cost' | null)}
            options={[
              { value: '', label: 'Bulk edit…' },
              { value: 'category', label: 'Category' },
              { value: 'vendor', label: 'Brand' },
              { value: 'cost', label: 'Cost' },
            ]}
            placeholder="Bulk edit…"
            className="text-xs min-w-[7rem]"
          />
          <button
            type="button"
            onClick={exitSelectMode}
            className="text-xs text-gray-400 hover:text-white ml-auto"
          >
            Cancel
          </button>
        </div>
      )}

      {bulkEditMode && selectedMods.size > 0 && (
        <div className="bg-orange-900/15 border border-orange-700/60 rounded-lg p-4">
          <h4 className="text-orange-400 font-medium mb-3 text-sm">
            Bulk edit {bulkEditMode} for {selectedMods.size} parts
          </h4>
          {bulkEditMode === 'category' && (
            <CustomDropdown
              value=""
              onChange={(value) => {
                if (value) {
                  setParsedModifications((prev) =>
                    prev.map((mod, index) =>
                      selectedMods.has(index)
                        ? { ...mod, category: value, category_confirmed: true }
                        : mod
                    )
                  );
                  setSelectedMods(new Set());
                  setBulkEditMode(null);
                }
              }}
              options={[
                { value: '', label: 'Choose category...' },
                ...availableCategories.map((cat) => ({ value: cat, label: cat })),
              ]}
              placeholder="Choose category..."
              className="text-sm"
            />
          )}
          {bulkEditMode === 'vendor' && (
            <div className="w-full max-w-xs flex flex-col gap-2">
              <VendorCombobox
                value={bulkVendorDraft}
                vendors={availableVendors}
                placeholder="Search or add brand…"
                onChange={setBulkVendorDraft}
              />
              <button
                type="button"
                disabled={!bulkVendorDraft.trim()}
                onClick={() => {
                  const value = bulkVendorDraft.trim();
                  if (!value) return;
                  setParsedModifications((prev) =>
                    prev.map((mod, index) =>
                      selectedMods.has(index) ? { ...mod, vendor: value } : mod
                    )
                  );
                  setSelectedMods(new Set());
                  setBulkEditMode(null);
                  setBulkVendorDraft('');
                }}
                className="text-xs px-3 py-2 rounded border border-white/[0.10] text-gray-200 disabled:opacity-40 min-h-[40px]"
              >
                Apply brand to selected
              </button>
            </div>
          )}
          {bulkEditMode === 'cost' && (
            <input
              type="number"
              placeholder="Enter cost..."
              onKeyDown={(e) => {
                if (e.key === 'Enter' && e.currentTarget.value) {
                  const cost = parseFloat(e.currentTarget.value);
                  setParsedModifications((prev) =>
                    prev.map((mod, index) => (selectedMods.has(index) ? { ...mod, cost } : mod))
                  );
                  setSelectedMods(new Set());
                  setBulkEditMode(null);
                }
              }}
              className="input text-sm w-32 !min-h-[40px]"
            />
          )}
          <button
            type="button"
            onClick={() => {
              setSelectedMods(new Set());
              setBulkEditMode(null);
            }}
            className="mt-2 text-xs text-gray-400 hover:text-gray-300"
          >
            Cancel
          </button>
        </div>
      )}

      {parsedModifications.length > 0 ? (
        <ImportReviewGroupedList
          modifications={parsedModifications}
          excludedIndices={excludedIndices}
          collapsedCategories={collapsedCategories}
          collapsedGroups={collapsedGroups}
          availableCategories={availableCategories}
          availableVendors={availableVendors}
          smartSuggestions={smartSuggestions}
          selectedMods={selectedMods}
          showBulkSelect={selectMode}
          visibleIndices={visibleIndices}
          pendingCategoryApply={pendingCategoryApply}
          onToggleCategory={onToggleCategory}
          onToggleGroup={onToggleGroup}
          onToggleIncluded={onToggleIncluded}
          onToggleSelect={(index) => {
            setSelectedMods((prev) => {
              const next = new Set(prev);
              if (next.has(index)) next.delete(index);
              else next.add(index);
              return next;
            });
          }}
          onEdit={onEdit}
          onCategoryChange={onCategoryChange}
          onApplyCategoryToSimilar={onApplyCategoryToSimilar}
          onDismissCategoryApply={onDismissCategoryApply}
          onRemove={onRemove}
        />
      ) : (
        <div className="text-center py-8 text-gray-400">
          <div className="text-lg mb-2">No parts found</div>
          <div className="text-sm">Try a different list or go back to edit.</div>
        </div>
      )}

      {errors.length > 0 && (
        <div className="bg-red-900/20 border border-red-700 rounded-lg p-4" role="alert">
          <h4 className="text-red-400 font-medium mb-2">Something needs attention</h4>
          <ul className="text-red-300 text-sm space-y-1">
            {errors.map((error, index) => (
              <li key={index}>• {error}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};
